"use client";

/**
 * AdminTransfer — 두 목록 간 항목 이동 (Ant Design Transfer 스타일)
 *
 * 각 패널 구성: ① 상단 건수 → ② 검색창 → ③ 표(헤더 전체선택 + 행 목록).
 * 행에는 이미지/설명을 넣을 수 있고, 목록은 스크롤된다.
 * pageSize 를 주면 패널별 클라이언트 페이지네이션을 쓴다. (서버 페이징 아님)
 *
 * 제어 컴포넌트: targetKeys(우측에 있는 key 배열) 와 onChange 로 제어한다.
 *
 * @example
 * const [targetKeys, setTargetKeys] = useState<string[]>([]);
 * <AdminTransfer
 *   dataSource={items}
 *   targetKeys={targetKeys}
 *   onChange={setTargetKeys}
 *   titles={["미부여", "부여됨"]}
 *   pageSize={10}
 * />
 */
import { type ReactNode, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { AdminPagination } from "../table/admin-pagination";

export interface TransferItem {
  key: string;
  /** 기본(컬럼 미지정) 렌더용 제목 */
  title?: string;
  /** 행 앞 이미지 URL (기본 렌더) */
  image?: string;
  /** 제목 아래 보조 설명 (기본 렌더) */
  description?: string;
  disabled?: boolean;
  /** 컬럼 모드에서 dataIndex 로 참조할 필드 */
  [field: string]: unknown;
}

/** 표 형태 컬럼 정의 (columns 지정 시) */
export interface TransferColumn {
  title: ReactNode;
  /** 표시할 필드명 (render 없으면 item[dataIndex]) */
  dataIndex?: string;
  /** 너비 (number=px, 미지정 시 1fr) */
  width?: number | string;
  /** 셀 커스텀 렌더 */
  render?: (item: TransferItem) => ReactNode;
}

export interface AdminTransferProps {
  dataSource: TransferItem[];
  /** 우측(선택됨) 패널에 있는 항목 key 배열 */
  targetKeys: string[];
  onChange: (targetKeys: string[]) => void;
  /** [좌측, 우측] 패널 제목 (기본 ["목록", "선택됨"]) */
  titles?: [ReactNode, ReactNode];
  /** 검색 입력 표시 (기본 true) */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** 리스트 높이(px, 기본 280) */
  listHeight?: number;
  /** 주면 패널별 클라이언트 페이지네이션 사용 */
  pageSize?: number;
  /** 표 컬럼 정의. 주면 헤더에 컬럼명 + 행에 표 형태로 렌더링 */
  columns?: TransferColumn[];
  /** 항목 표시 커스터마이징 (columns 미지정 시, 기본: 이미지 + 제목/설명) */
  render?: (item: TransferItem) => ReactNode;
  className?: string;
}

export function AdminTransfer({
  dataSource,
  targetKeys,
  onChange,
  titles = ["목록", "선택됨"],
  searchable = true,
  searchPlaceholder = "검색...",
  listHeight = 280,
  pageSize,
  columns,
  render,
  className,
}: AdminTransferProps) {
  const [checked, setChecked] = useState<string[]>([]);

  const targetSet = new Set(targetKeys);
  const leftItems = dataSource.filter((d) => !targetSet.has(d.key));
  const rightItems = dataSource.filter((d) => targetSet.has(d.key));

  const checkedSet = new Set(checked);
  const leftChecked = leftItems.filter(
    (i) => checkedSet.has(i.key) && !i.disabled
  );
  const rightChecked = rightItems.filter(
    (i) => checkedSet.has(i.key) && !i.disabled
  );

  const moveRight = () => {
    const moving = leftChecked.map((i) => i.key);
    onChange([...targetKeys, ...moving]);
    setChecked((c) => c.filter((k) => !moving.includes(k)));
  };
  const moveLeft = () => {
    const moving = new Set(rightChecked.map((i) => i.key));
    onChange(targetKeys.filter((k) => !moving.has(k)));
    setChecked((c) => c.filter((k) => !moving.has(k)));
  };

  const toggle = (key: string) =>
    setChecked((c) =>
      c.includes(key) ? c.filter((k) => k !== key) : [...c, key]
    );

  const toggleAll = (keys: string[], next: boolean) =>
    setChecked((c) => {
      const set = new Set(c);
      if (next) keys.forEach((k) => set.add(k));
      else keys.forEach((k) => set.delete(k));
      return [...set];
    });

  const panelProps = {
    checkedKeys: checkedSet,
    onToggle: toggle,
    onToggleAll: toggleAll,
    searchable,
    searchPlaceholder,
    listHeight,
    pageSize,
    columns,
    render,
  };

  return (
    <div className={cn("flex items-stretch gap-3", className)}>
      <TransferPanel title={titles[0]} items={leftItems} {...panelProps} />

      <div className="flex flex-col justify-center gap-2">
        <Button
          type="button"
          // 활성: solid(primary), 비활성: outline+흐림 → 상태가 또렷이 구분된다
          variant={leftChecked.length > 0 ? "default" : "outline"}
          size="icon"
          aria-label="오른쪽으로 이동"
          disabled={leftChecked.length === 0}
          onClick={moveRight}
          className="disabled:opacity-40"
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          type="button"
          variant={rightChecked.length > 0 ? "default" : "outline"}
          size="icon"
          aria-label="왼쪽으로 이동"
          disabled={rightChecked.length === 0}
          onClick={moveLeft}
          className="disabled:opacity-40"
        >
          <ChevronLeft className="size-4" />
        </Button>
      </div>

      <TransferPanel title={titles[1]} items={rightItems} {...panelProps} />
    </div>
  );
}

interface TransferPanelProps {
  title: ReactNode;
  items: TransferItem[];
  checkedKeys: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: (keys: string[], next: boolean) => void;
  searchable: boolean;
  searchPlaceholder: string;
  listHeight: number;
  pageSize?: number;
  columns?: TransferColumn[];
  render?: (item: TransferItem) => ReactNode;
}

/** 항목의 검색 대상 텍스트 (컬럼이 있으면 컬럼 값들, 없으면 제목/설명) */
function itemText(item: TransferItem, columns?: TransferColumn[]): string {
  if (columns?.length) {
    return columns
      .map((c) => (c.dataIndex ? String(item[c.dataIndex] ?? "") : ""))
      .join(" ");
  }
  return `${item.title ?? ""} ${item.description ?? ""}`;
}

function TransferPanel({
  title,
  items,
  checkedKeys,
  onToggle,
  onToggleAll,
  searchable,
  searchPlaceholder,
  listHeight,
  pageSize,
  columns,
  render,
}: TransferPanelProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const q = search.trim().toLowerCase();
  const visible = q
    ? items.filter((i) => itemText(i, columns).toLowerCase().includes(q))
    : items;

  // 컬럼 모드: [체크박스 2rem, ...컬럼 너비] 그리드 트랙
  const useColumns = !!columns?.length;
  const gridTemplate = useColumns
    ? [
        "1.5rem",
        ...columns!.map((c) =>
          c.width == null
            ? "minmax(0,1fr)"
            : typeof c.width === "number"
              ? `${c.width}px`
              : c.width
        ),
      ].join(" ")
    : undefined;

  // 전체선택은 검색결과(visible) 전체의 활성 항목 기준
  const enabled = visible.filter((i) => !i.disabled);
  const checkedCount = visible.filter((i) => checkedKeys.has(i.key)).length;
  const allChecked =
    enabled.length > 0 && enabled.every((i) => checkedKeys.has(i.key));
  const someChecked = checkedCount > 0 && !allChecked;

  // 페이지네이션 (선택적, 클라이언트)
  const curPage = pageSize ? Math.min(page, Math.max(1, Math.ceil(visible.length / pageSize))) : 1;
  const rows = pageSize
    ? visible.slice((curPage - 1) * pageSize, curPage * pageSize)
    : visible;

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
      {/* ① 건수 */}
      <div className="flex items-center gap-2 px-3 py-2 text-sm">
        <span className="font-medium">{title}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          총 <b className="text-foreground">{visible.length}</b>건
          {checkedCount > 0 && (
            <>
              {" · 선택 "}
              <b className="text-foreground">{checkedCount}</b>
            </>
          )}
        </span>
      </div>

      {/* ② 검색 (테두리 박스로 감싸고 여백) */}
      {searchable && (
        <div className="border-t px-3 py-2">
          <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={searchPlaceholder}
              className="h-full w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
      )}

      {/* ③ 표 헤더 (전체선택 + 컬럼명) */}
      {useColumns ? (
        <div
          className="grid items-center gap-x-3 border-t border-b bg-muted px-4 py-2 text-xs font-medium text-muted-foreground"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <Checkbox
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={(v) =>
              onToggleAll(
                enabled.map((i) => i.key),
                !!v
              )
            }
            aria-label="전체 선택"
            disabled={enabled.length === 0}
          />
          {columns!.map((c, ci) => (
            <span key={ci} className="truncate">
              {c.title}
            </span>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 border-t border-b bg-muted px-4 py-2 text-xs font-medium text-muted-foreground">
          <Checkbox
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={(v) =>
              onToggleAll(
                enabled.map((i) => i.key),
                !!v
              )
            }
            aria-label="전체 선택"
            disabled={enabled.length === 0}
          />
          <span>전체</span>
          <span className="ml-auto">
            {checkedCount}/{visible.length}
          </span>
        </div>
      )}

      {/* ③ 행 목록 (스크롤) */}
      <ul
        className="overflow-y-auto"
        style={{ height: listHeight }}
      >
        {rows.length === 0 ? (
          <li className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
            항목이 없습니다.
          </li>
        ) : (
          rows.map((item) => (
            <li key={item.key} className="border-b last:border-0">
              <label
                className={cn(
                  "cursor-pointer text-sm transition-colors hover:bg-accent/50",
                  useColumns
                    ? "grid items-center gap-x-3 px-4 py-2"
                    : "flex items-center gap-2 px-4 py-2",
                  item.disabled && "cursor-not-allowed opacity-50"
                )}
                style={
                  useColumns
                    ? { gridTemplateColumns: gridTemplate }
                    : undefined
                }
              >
                <Checkbox
                  checked={checkedKeys.has(item.key)}
                  onCheckedChange={() => onToggle(item.key)}
                  disabled={item.disabled}
                />
                {useColumns ? (
                  columns!.map((c, ci) => (
                    <span key={ci} className="min-w-0 truncate">
                      {c.render
                        ? c.render(item)
                        : c.dataIndex
                          ? (item[c.dataIndex] as ReactNode)
                          : null}
                    </span>
                  ))
                ) : render ? (
                  render(item)
                ) : (
                  <>
                    {item.image && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={item.image}
                        alt=""
                        className="size-8 shrink-0 rounded object-cover"
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{item.title}</span>
                      {item.description && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </label>
            </li>
          ))
        )}
      </ul>

      {/* 페이지네이션 (선택적) */}
      {pageSize && visible.length > pageSize && (
        <div className="border-t px-3 py-2">
          <AdminPagination
            page={curPage}
            pageSize={pageSize}
            total={visible.length}
            onPageChange={setPage}
            showJump={false}
          />
        </div>
      )}
    </div>
  );
}
